<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h4 class="mb-0">
        Кто сейчас в домене
      </h4>
      <div>
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="loading"
          class="mr-1"
          @click="load"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          Обновить
        </b-button>
        <b-button
          variant="outline-danger"
          size="sm"
          :disabled="loading || rows.length < 2"
          @click="revokeOthers"
        >
          Отозвать все чужие
        </b-button>
      </div>
    </div>

    <b-alert
      variant="info"
      show
      class="mb-1"
    >
      <div class="alert-body">
        Одна строка — одна живая сессия, а не человек: тот, кто открыл интерфейс и подключил
        ИИ-клиента, занимает две. Отзыв гасит сессии выбранного пользователя немедленно —
        и в браузере, и у его интеграций.
      </div>
    </b-alert>

    <div v-if="error">
      <b-alert
        variant="danger"
        show
      >
        <div class="alert-body">
          {{ error }}
        </div>
      </b-alert>
    </div>

    <template v-else>
      <p class="mb-1">
        Живых сессий: <b>{{ summary.sessions }}</b>,
        человек: <b>{{ summary.users }}</b>
        <span v-if="summary.by_token">, из них по токену доступа: <b>{{ summary.by_token }}</b></span>
      </p>

      <b-table
        v-if="rows.length"
        :items="rows"
        :fields="fields"
        responsive
        striped
        small
      >
        <template #cell(origin)="data">
          <b-badge :variant="data.item.origin === 'token' ? 'light-warning' : 'light-secondary'">
            {{ data.item.origin === 'token' ? 'токен' : 'пароль' }}
          </b-badge>
          <div
            v-if="data.item.tokenid"
            class="text-muted font-small-2"
          >
            {{ data.item.tokenid }}
          </div>
        </template>
        <template #cell(sessionsid)="data">
          <span
            v-if="data.item.sessionsid"
            class="text-muted font-small-2"
          >{{ data.item.sessionsid }}</span>
          <span
            v-else
            class="text-muted font-small-2"
            title="Сессия выдана до появления идентификатора — поштучно её не погасить"
          >—</span>
        </template>
        <template #cell(actions)="data">
          <b-button
            variant="flat-danger"
            size="sm"
            :disabled="loading || isMySession(data.item)"
            :title="revokeHint(data.item)"
            @click="revokeRow(data.item)"
          >
            {{ data.item.origin === 'token' ? 'Отозвать токен' : 'Завершить' }}
          </b-button>
          <b-button
            v-if="!isMyUser(data.item)"
            variant="flat-secondary"
            size="sm"
            :disabled="loading"
            title="Отозвать ВСЕ сессии этого пользователя"
            @click="revokeUser(data.item)"
          >
            Все его
          </b-button>
        </template>
      </b-table>

      <b-alert
        v-else-if="!loading"
        variant="secondary"
        show
      >
        <div class="alert-body">
          Сейчас в домене никого нет.
        </div>
      </b-alert>
    </template>
  </b-card>
</template>

<script>
import {
  BCard, BButton, BTable, BAlert, BSpinner, BBadge,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useCubus from '@/cubus/services/useCubus'
import useJwt from '@/cubus/jwt/useJwt'
import store from '@/store'

/**
 * PROP-218: список работающих в домене — пара к отзыву сессий (`BUG-099`).
 *
 * Гасить сессии платформа умела, а видеть людей было нельзя ниоткуда, поэтому кнопкой отзыва
 * никто и не пользовался: администратор не знал, кого выключает. Здесь список и кнопка стоят
 * рядом — это и есть весь смысл страницы.
 *
 * Запрос собирает сама страница: `usersid` кладётся ВНУТРЬ `record`. Мимо этой формы процедура
 * не видит параметра и гасит ВСЕ сессии домена — грабли, на которые я наступил при приёмке
 * бэкенда. Кнопка не должна полагаться на то, что администратор помнит форму запроса.
 */
export default {
  components: {
    BCard, BButton, BTable, BAlert, BSpinner, BBadge,
  },
  data() {
    return {
      loading: false,
      error: '',
      rows: [],
      summary: { sessions: 0, users: 0, by_token: 0 },
      fields: [
        { key: 'name', label: 'Пользователь' },
        { key: 'roles', label: 'Роли' },
        { key: 'origin', label: 'Как вошёл' },
        { key: 'created_str', label: 'Вход' },
        { key: 'lastseen_str', label: 'Последний запрос' },
        { key: 'ttl_min', label: 'Осталось, мин' },
        { key: 'sessionsid', label: 'Сессия' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    mySid() {
      const s = store.getters['cubus-store/SESSION']
      return (s && s.user && s.user.sid) || ''
    },
    /** PROP-235: идентификатор ТЕКУЩЕЙ сессии — приходит в `whoami` вместе с остальным. */
    mySessionSid() {
      const s = store.getters['cubus-store/SESSION']
      return (s && s.sessionsid) || ''
    },
  },
  created() {
    this.load()
  },
  methods: {
    toast(title, variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckIcon', variant, text,
        },
      })
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await useCubus.runProcedure(this, {
          objectsid: 'proc_active_sessions',
          record: {},
        })
        // Процедура отдаёт свой JSON внутри `result`; у отказа приходит текст ошибки.
        const data = (res && res.result) || res || {}
        if (data.error) {
          this.error = data.error
          this.rows = []
        } else {
          this.rows = data.rows || []
          this.summary = {
            sessions: data.sessions || this.rows.length,
            users: data.users || 0,
            by_token: data.by_token || 0,
          }
        }
      } catch (e) {
        this.error = (e && e.message) || 'Не удалось получить список сессий'
      } finally {
        this.loading = false
      }
    },
    isMyUser(row) {
      return row.usersid === this.mySid
    },
    /**
     * PROP-235: «моё» — это ТЕКУЩАЯ сессия, а не всё, что принадлежит мне.
     *
     * Первая редакция страницы выключала кнопку по `usersid`, и это ломало ровно тот сценарий,
     * ради которого тикет заводился: у сотрудника две вкладки, обе его — значит обе кнопки
     * выключены, и погасить лишнюю нельзя ниоткуда. Поймано боевой приёмкой 16.08.
     *
     * Опора — `sessionsid`, он у сессии свой. Если у строки его нет (сессия выдана прежним
     * билдом), различить её от текущей нечем: тогда возвращаемся к прежнему признаку по
     * пользователю и оставляем кнопку выключенной — лучше не дать погасить, чем разлогинить
     * человека, который нажал на чужую с виду строку.
     */
    isMySession(row) {
      if (row.sessionsid && this.mySessionSid) return row.sessionsid === this.mySessionSid
      return this.isMyUser(row)
    },
    revokeHint(row) {
      if (this.isMySession(row)) return 'Это ваша текущая сессия'
      if (row.origin === 'token') return 'Сессия создана личным токеном — гасится отзывом самого токена'
      if (!row.sessionsid) return 'Сессия выдана до появления идентификатора — поштучно не гасится, только все сессии пользователя'
      return 'Завершить эту сессию'
    },
    /**
     * PROP-235: одна строка — одно действие, но действия РАЗНЫЕ по природе.
     *
     * Сессию от пароля гасим поштучно по её идентификатору. Сессию от токена гасить поштучно
     * бессмысленно: она у токена одна и тут же родится заново при следующем запросе — правильный
     * ответ здесь отзыв самого токена, иначе мы бы завели второй способ делать то же самое.
     * Сессия без идентификатора (выдана прежним билдом) честно уводит к отзыву всех сессий
     * пользователя — с явным предупреждением, а не молча.
     */
    async revokeRow(row) {
      const who = row.name || row.username
      if (row.origin === 'token') {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Сессия создана личным токеном «${row.tokenid}». Отозвать сам токен? `
          + `Все интеграции «${who}», где он вставлен, перестанут работать.`)) return
        await this.revokeToken(row)
        return
      }
      if (!row.sessionsid) {
        // eslint-disable-next-line no-alert
        if (!window.confirm('У этой сессии нет идентификатора (выдана до обновления), поштучно её '
          + `не погасить. Отозвать ВСЕ сессии «${who}»?`)) return
        await this.revoke({ usersid: row.usersid }, `Сессии «${who}» отозваны`)
        return
      }
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Завершить эту сессию «${who}»? Остальные его сессии продолжат работать.`)) return
      await this.revoke({ sessionsid: row.sessionsid }, 'Сессия завершена')
    },
    async revokeToken(row) {
      this.loading = true
      try {
        const { data } = await useJwt.apiToken({ action: 'revoke', usersid: row.usersid })
        if (data && data.error) this.toast('Отозвать не удалось', 'danger', data.error)
        else this.toast('Токен отозван', 'success', `Идентификатор: ${row.tokenid}`)
      } catch (e) {
        this.toast('Отозвать не удалось', 'danger', this.errText(e))
      } finally {
        this.loading = false
        await this.load()
      }
    },
    errText(e) {
      return (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || ''
    },
    async revokeUser(row) {
      const who = row.name || row.username
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Отозвать сессии пользователя «${who}»? Он выйдет из системы немедленно, `
        + 'вместе со своими подключёнными интеграциями.')) return
      await this.revoke({ usersid: row.usersid }, `Сессии «${who}» отозваны`)
    },
    async revokeOthers() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Отозвать ВСЕ чужие сессии в домене? Ваша сессия сохранится, '
        + 'остальные пользователи и их интеграции выйдут немедленно.')) return
      await this.revoke({}, 'Чужие сессии отозваны')
    },
    async revoke(record, okText) {
      this.loading = true
      try {
        const res = await useCubus.runProcedure(this, {
          objectsid: 'proc_clear_sessions',
          record,
        })
        const data = (res && res.result) || res || {}
        if (data.error) this.toast('Отозвать не удалось', 'danger', data.error)
        else this.toast(okText, 'success', `Погашено сессий: ${data.deleted != null ? data.deleted : '—'}`)
      } catch (e) {
        this.toast('Отозвать не удалось', 'danger', (e && e.message) || '')
      } finally {
        this.loading = false
        await this.load()
      }
    },
  },
}
</script>
